import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import { Cart } from "@insite/client-framework/Services/CartService";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import {
    CartLineCollectionModel,
    CartLineModel,
    CartModel,
    OrderLineModel,
    ProductInventoryDto,
    ProductModel,
    ShipToModel,
    WarehouseDto
} from "@insite/client-framework/Types/ApiModels";

const lastTrackedUrl = "";
let lastProductImpression: any;
export function addToCartTracking(product: CartLineModel, productInfo: ProductInfo, state: ApplicationState) {
    try {
        const qtyOnHand = product?.qtyOnHand ?? 0;
        // Condition to show stock as "DirectShip" when total qty<=0 and isDirectShipFromManufacturer is true
        const availabilityMessage = Number(qtyOnHand) > 0
            ? "In Stock"
            : product?.properties?.isDirectShipFromManufacturer.toLowerCase() === "true"
                ? "DirectShip" : "Out Of Stock";
        let productCondition;
        if (product.productId) {
            const productData = getProductState(state, product.productId).value;
            productCondition = productData?.properties?.condition || product?.properties?.cartLineCondition;
        }
        const currentUrl = window.document.URL;
        const pageSourceResult = currentUrl.toLowerCase().includes("search") ? 'Search Results' :
            currentUrl.toLowerCase().includes("checkout/review") ? 'Order Review' : currentUrl.toLowerCase().includes("brand") ? 'Brand Results'
                : currentUrl.toLowerCase().includes("quote/confirmation") ? 'Quote Confirmation' : currentUrl.toLowerCase().includes("mylists") ? "Product Wish List" : currentUrl.toLowerCase().includes("category") ? 'Category Results' : currentUrl.toLowerCase().includes("staticlist") ? "Shared Cart" : product.properties?.productSourceActionField ?? "Product Details";
        let actionField;
        if (pageSourceResult === "Shared Cart") {
            const wishListState = getWishListState(state, state.pages.staticList.wishListId);
            const wishList = wishListState.value;
            actionField = {
                id: wishList?.id,
                list: pageSourceResult,
                action: "add",
            };
        }
        else {
            actionField = {
                list: pageSourceResult,
                action: "add",
            };
        }
        const hazardousProduct = product?.properties?.isHazardousGood === "TRUE" ? "Yes" : "";
        const vendorStockInfo = getVendorStockStatus(product, productInfo);
        const dataLayer = (window as any).dataLayer || [];
        dataLayer.push({ ecommerce: null });
        dataLayer.push({
            event: "addToCart",
            ecommerce: {
                currencyCode: state.context.session.currency?.currencyCode,
                add: {
                    actionField,
                    products: [
                        {
                            name: product.shortDescription,
                            id: product.erpNumber,
                            price: product.pricing?.actualPrice,
                            brand: product.properties?.cartLineBrandName,
                            condition: productCondition || "",
                            quantity: product.qtyOrdered,
                            stock: availabilityMessage,
                            category: product.properties?.cartlineCategoryName,
                            position: product?.properties?.productListPosition || "",
                            vendorStockStatus: vendorStockInfo.vendorStockStatus,
                            vendorStockQty: vendorStockInfo.vendorStockQty !== 0 ? vendorStockInfo.vendorStockQty : "",
                            Hazardous: hazardousProduct,
                        },
                    ],
                },
            },
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        logger.debug(err);
    }
}
export function addToCartCollectionTracking(products: CartLineCollectionModel, state: ApplicationState) {
    try {
        const currentUrl = window.document.URL;
        const pageSourceResult = currentUrl.toLowerCase().includes("search") ? 'Search Results' : currentUrl.toLowerCase().includes("checkout/review") ? 'Order Review' : currentUrl.toLowerCase().includes("brand") ? 'Brand Results'
            : currentUrl.toLowerCase().includes("quote/confirmation") ? 'Quote Confirmation' : currentUrl.toLowerCase().includes("category") ? 'Category Results' : currentUrl.toLowerCase().includes("mylists") ? "Product Wish List" : currentUrl.toLowerCase().includes("staticlist") ? "Shared Cart" : "Product Details";
        const dataLayer = (window as any).dataLayer || [];
        dataLayer.push({ ecommerce: null });
        const dataLayerProducts: any[] = [];
        products?.cartLines?.forEach(cartLine => {
            let productCondition = "";
            if (cartLine.productId) {
                const productData = getProductState(state, cartLine.productId).value;
                productCondition = productData?.properties?.condition || cartLine?.properties?.cartLineCondition;
            }
            dataLayerProducts.push({
                name: cartLine.shortDescription,
                id: cartLine.erpNumber,
                price: cartLine.pricing?.actualPrice,
                brand: cartLine.properties?.cartLineBrandName,
                condition: productCondition,
                quantity: cartLine.qtyOrdered,
                category: cartLine.properties?.cartlineCategoryName,
                position: cartLine?.line,
            });
        });
        let actionField;
        if (pageSourceResult === "Shared Cart") {
            const wishListState = getWishListState(state, state.pages.staticList.wishListId);
            const wishList = wishListState.value;
            actionField = {
                id: wishList?.id,
                list: pageSourceResult,
                action: "add",
            }
        }
        else {
            actionField = {
                list: pageSourceResult,
                action: "add",
            };
        }
        const addToCartEvent = {
            event: "addToCart",
            ecommerce: {
                currencyCode: state.context.session.currency?.currencyCode,
                add: {
                    actionField,
                    products: dataLayerProducts,
                },
            },
        };
        dataLayer.push(addToCartEvent);
    } catch (err) {
        // eslint-disable-next-line no-console
        logger.debug(err);
    }
}
export function productPromotionTracking(SlideModel: any) {
    try {
        const dataLayer = (window as any).dataLayer || [];
        const promotionImpressionsEvent = dataLayer.find((x: any) => x?.event === "promotionImpressions");
        if (typeof promotionImpressionsEvent === "undefined") {
            const promotionImpressionsEvent = {
                event: "promotionImpressions",
                ecommerce: {
                    promotions: [],
                },
            };
            dataLayer.push(promotionImpressionsEvent);
        }
        SlideModel.forEach((SlideModelData: any, indexNumb: any) => {
            let promotions = promotionImpressionsEvent?.ecommerce?.promotions.find((p: any) => p.id === SlideModelData.fields.slideTitle);
            if (typeof promotions === "undefined") {
                const data = {
                    id: SlideModelData.fields.slideTitle,            // ID or Name is required.
                    name: SlideModelData.fields.boxcontentheading,
                    creative: SlideModelData.fields.boxcontentsubheading,
                    position: indexNumb + 1,
                };
                promotionImpressionsEvent?.ecommerce?.promotions?.push(data);
            }
            else {
                const data = {
                    id: SlideModelData.fields.slideTitle,            // ID or Name is required.
                    name: SlideModelData.fields.boxcontentheading,
                    creative: SlideModelData.fields.boxcontentsubheading,
                    position: indexNumb + 2,
                };
                promotions = data;
            }
        })
    } catch (err) {
        // eslint-disable-next-line no-console
        logger.debug(err);
    }
}
export function productClickTracking(
    product: CartLineModel | ProductContextModel | OrderLineModel,
    carouselTitle: string,
    carouselType: string,
    category: Category | undefined,
    currentPageUrl: string,
    state: ApplicationState,
    warehouses: WarehouseDto[] | undefined,
    inventory?: ProductInventoryDto,
    itemLinkClick?: string | undefined,
) {
    try {
        const description =
            "product" in product
                ? product.product.productTitle
                : product.shortDescription || ("description" in product ? product.description : "");
        let sku = "product" in product ? product.product.productNumber : "productErpNumber" in product ? product.productErpNumber : "";
        const position = "product" in product ? product.idx! + 1 : "line" in product ? product.line : 0;
        const productId = "product" in product ? product.product.id : "productId" in product ? product.productId : "";
        if (sku === undefined || sku === "") {
            sku = "productName" in product ? String(product.productName) : "erpNumber" in product ? String(product.erpNumber) : "" ;
        }
        let condition = "";

        if ("product" in product) {
            condition = product.product?.properties?.condition ?? "";
        } else if ("line" in product) {
            condition = product.properties?.cartLineCondition ?? product.properties?.condition ?? "";
        } else if ("lineNumber" in product) {
            condition = product.properties?.condition ?? "";
        }
        
        let listName = "";
        if (carouselTitle !== undefined && category?.shortDescription !== undefined) {
            listName = carouselTitle;
        } else if (carouselType !== undefined && category?.shortDescription === undefined) {
            listName = carouselTitle;
        } else if (carouselTitle === undefined && category?.shortDescription !== undefined) {
            listName = "Category Results";
        }
        listName = currentPageUrl.includes("Search")
            ? "Search Results"
            : currentPageUrl.includes("Cart")
                ? "Cart Lines"
                : currentPageUrl.includes("category/All/")
                    ? "Brand Results"
                    : currentPageUrl.includes("category/all")
                        ? "Brand Results"
                        : currentPageUrl.includes("SavedOrders")
                            ? "Saved Order Details Page"
                            : currentPageUrl.includes("Orders/Details")
                                ? "Order History Details"
                                : currentPageUrl.includes("MyLists")
                                    ? "Product Wish List"
                                    : currentPageUrl.includes("CheckoutReviewAndSubmit")
                                        ? "Order Review"
                                        : currentPageUrl.includes("OrderConfirmation")
                                            ? "Order Confirmation"
                                            : currentPageUrl.includes("Quote/QuoteDetail")
                                            ? "Quote Details Page"
                                            : listName;
        const index = "product" in product ? product.idx : 0;
        let productInfo = "product" in product ? product.productInfo : undefined;
        if (productInfo === undefined) {
            const productData = getProductState(state, productId as string).value as ProductModel;
            if (productData) {
                productInfo =
                    state.pages.productDetails.productInfosById?.[productId as string] ??
                    createFromProduct(productData);
            }
        }

        if(currentPageUrl.includes("Orders/Details") || currentPageUrl.includes("Quote/QuoteDetail")) {
            if(productInfo === undefined) {
                productInfo = {
                productId: "productId" in product ? product.productId ?? '' : '',
                inventory,
                qtyOrdered: "qtyOrdered" in product ? product.qtyOrdered ?? 0:0,
                productDetailPath: '',
                unitOfMeasure: "unitOfMeasure" in product ? product.unitOfMeasure ?? '' : ''
                }
            }
            else {
                productInfo = { ...productInfo, inventory};
            }
        }

        let vendorStockStatus = "";
        let vendorStockQty = "";
        if (
            (productInfo?.inventory?.inventoryWarehousesDtos &&
                productInfo?.inventory?.inventoryWarehousesDtos.length > 0) || (warehouses)
        ) {
            const warehouseDtos = productInfo?.inventory?.inventoryWarehousesDtos ? productInfo?.inventory?.inventoryWarehousesDtos[0].warehouseDtos : warehouses;
            if (warehouseDtos) {
                for (const warehouseData of warehouseDtos) {
                    if (warehouseData.name.toLowerCase() === "vendor stock" || warehouseData.name.toLowerCase() === "blind ship") {
                        vendorStockQty = warehouseData.qty.toString();
                    }
                    if (warehouseData.qty > 0 && warehouseData.name.toLowerCase() === "blind ship") {
                        vendorStockStatus = "blindship";
                        break;
                    }
                    if (
                        "product" in product ? product.product.properties.isDirectShipFromManufacturer.toLowerCase() === "true" : "isDirectShipFromManufacturer" in product.properties ? product.properties.isDirectShipFromManufacturer.toLowerCase() === "true" : "false"
                    ) {
                        if (
                            (warehouseData.qty > 0 && warehouseData.name.toLowerCase() === "vendor stock") ||
                            (warehouseData.qty === 0 && warehouseData.name.toLowerCase() === "blind ship")
                        ) {
                            vendorStockStatus = "crossdock";
                            break;
                        }
                    }
                }
            }
        }
        if ("product" in product ? product.product.properties.isDirectShipFromManufacturer.toLowerCase() === "true" : "isDirectShipFromManufacturer" in product.properties ? product.properties.isDirectShipFromManufacturer.toLowerCase() === "true" : "false") {
            vendorStockStatus = "directship";
        }

        const stkQty = currentPageUrl.includes("Orders/Details") || currentPageUrl.includes("Quote/QuoteDetail")
                        ?
                        productInfo?.inventory?.qtyOnHand ?? 0
                        :
                        "product" in product 
                            ? product.productInfo.inventory?.qtyOnHand 
                            : "qtyOnHand" in product 
                                ? 
                                product.qtyOnHand 
                                : "0";
        const stock = currentPageUrl.includes("Orders/Details")
                        ?
                        productInfo?.inventory?.qtyOnHand !== undefined &&
                        productInfo?.inventory?.qtyOnHand <= 0 &&
                        "properties" in product && product.properties.isDirectShipFromManufacturer.toLowerCase() === "true"
                        ? "DirectShip"
                        : (
                            productInfo?.inventory?.inventoryAvailabilityDtos?.find(
                                o =>
                                    o.unitOfMeasure.toLowerCase() ===
                                    (productInfo?.unitOfMeasure?.toLowerCase() || ""),
                            )?.availability || undefined
                        )?.message ?? ""
                        :
                        "product" in product
                            ? // Condition to show stock as "DirectShip" when total qty<=0 and isDirectShipFromManufacturer is true
                            product.productInfo?.inventory?.qtyOnHand !== undefined &&
                                product.productInfo?.inventory?.qtyOnHand <= 0 &&
                                product.product.properties.isDirectShipFromManufacturer.toLowerCase() === "true"
                                ? "DirectShip"
                                : (
                                    product.productInfo?.inventory?.inventoryAvailabilityDtos?.find(
                                        o =>
                                            o.unitOfMeasure.toLowerCase() ===
                                            (product.productInfo?.unitOfMeasure?.toLowerCase() || ""),
                                    )?.availability || undefined
                                )?.message
                            : "";
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "productClick",
                ecommerce: {
                    click: {
                        actionField: { list: listName, action: "click" }, // Optional list property.
                        products: [
                            {
                                name: description,
                                id: sku,
                                price:
                                    "pricing" in product
                                        ? product.pricing?.actualPrice
                                        : "product" in product
                                            ? product.productInfo.pricing?.actualPrice
                                            : product.unitNetPrice,
                                brand: "product" in product ? product.product.brand?.name : product.brand?.name,
                                condition,
                                category: category?.shortDescription ? category?.shortDescription : "properties" in product ? product.properties?.categoryName ?? "" : "",
                                stock,
                                stockqty: stkQty,
                                position: String(index) !== "undefined" ? Number(index) + 1 : position,
                                vendorstockstatus: vendorStockStatus,
                                vendorstockqty: vendorStockQty,
                                item_link_click: itemLinkClick,
                            },
                        ],
                    },
                },
            });
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        logger.debug(err);
    }
}
export function productStockAndInventoryTracking(
    productInfo: ProductInfo,
    productModel: ProductModel,
    categoryName: string,
) {
    try {
        const dataLayer = (window as any).dataLayer || [];
        let notFound = false;
        dataLayer.forEach((layer: any) => {
            if (layer.event === "productDetail") {
                notFound = false;
            }
        });
        const description = productModel.productTitle ?? "";
        const productNumber = productModel.productNumber ?? "";
        const productPrice = productInfo.pricing?.actualPrice ?? productModel.unitListPrice;
        const brandName = productModel.brand?.name ?? "";
        const qtyOnHand = productInfo.inventory?.qtyOnHand ?? 0;
        const availabilityMessage =
            (
                productInfo?.inventory?.inventoryAvailabilityDtos?.find(
                    o => o.unitOfMeasure.toLowerCase() === (productInfo?.unitOfMeasure?.toLowerCase() || ""),
                )?.availability || undefined
            )?.message ?? "";
        if (notFound) {
            const productDetailsEvent = {
                event: "productDetail",
                ecommerce: {
                    detail: {
                        products: [
                            {
                                name: description,
                                category: categoryName,
                                id: productNumber,
                                price: productPrice,
                                brand: brandName,
                                stock: availabilityMessage,
                                stockqty: qtyOnHand,
                            }
                        ]
                    },
                },
            };
            dataLayer.push(productDetailsEvent);
        }
        else {
            let anyImpression: any;
            dataLayer.forEach((layer: any) => {
                if (layer.event === "productDetail") {
                    anyImpression = layer?.ecommerce?.detail?.products.find((p: any) => p.id === productNumber);
                    return { anyImpression };
                }
            });
            if (anyImpression === "undefined" || anyImpression === undefined) {
                const productDetailsEvent = {
                    event: "productDetail",
                    ecommerce: {
                        detail: {
                            products: [
                                {
                                    name: description,
                                    category: categoryName,
                                    id: productNumber,
                                    price: productPrice,
                                    brand: brandName,
                                    stock: availabilityMessage,
                                    stockqty: qtyOnHand,
                                }
                            ]
                        },
                    },
                };
                dataLayer.push(productDetailsEvent);
            }
            else {
                const latestProductDetailsEvent = dataLayer.findLast((x: any) => x?.event === "productDetail");
                latestProductDetailsEvent?.ecommerce?.detail?.products?.pop();
                latestProductDetailsEvent?.ecommerce?.detail?.products.push({
                    name: description,
                    category: categoryName,
                    id: productNumber,
                    price: productPrice,
                    brand: brandName,
                    stock: availabilityMessage,
                    stockqty: qtyOnHand,
                });
            }
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        //   logger.debug("Datalayer exception=>", err);
    }
}
function getDataLayer() {
    return (window as any).dataLayer || [];
}
export function trackCompletedOrder(cart: CartModel, state: ApplicationState, cartToUpdate?: CartModel, shipTo?: ShipToModel) {
    if (IS_SERVER_SIDE) {
        return;
    }
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }
    const dataLayerProducts: any[] = [];
    cartToUpdate?.cartLines?.forEach(cartLine => {
        let productCondition = "";
        if (cartLine.productId) {
            const productData = getProductState(state, cartLine.productId).value;
            productCondition = productData?.properties?.condition || cartLine?.properties?.cartLineCondition;
        }
        dataLayerProducts.push({
            id: cartLine.erpNumber,
            name: cartLine.shortDescription,
            price: cartLine?.pricing?.unitNetPrice ?? "",
            brand: cartLine?.brand?.name,
            condition: productCondition,
            category: cartLine.properties["cartlineCategoryName"],
            quantity: cartLine.qtyOrdered,
            list: cartLine?.properties?.productSourceActionField || "",
            listid: cartLine?.properties?.listIdField || "",
            position: cartLine.properties["productListPosition"],
            coupon: "",
        });
    });
    const isGuestOrderData = cart?.properties?.isGuestOrderData;
    const customerEmailForLoggedinUse = cart?.initiatedByUserName;
    const customerEmailData = isGuestOrderData === 'No' ? customerEmailForLoggedinUse : shipTo?.email;
    const data = {
        event: "transactionComplete",
        purchase: {
            actionField: {
                id: cart.orderNumber,
                affiliation: "Standard Checkout",
                revenue: cart.orderGrandTotal,
                tax: cart.totalTax,
                shipping: cart.shippingAndHandling,
                coupon: "",
                checkoutOption: {
                    actionField: { option: { shipping_method: cart.shipVia?.description } },
                },
                action: "purchase",
                isGuestCheckout: isGuestOrderData,
                customerEmail: customerEmailData
            },
            products: dataLayerProducts,
        },
    };
    const ecommerceObj = { event: "transactionComplete", ecommerce: data };
    dataLayer.push({ ecommerce: null });
    dataLayer.push(ecommerceObj);
    // dataLayer.push(data);
}
export function checkoutCartTracking(cart: CartModel) {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }
    const dataLayerProducts: any[] = [];
    cart?.cartLines?.forEach(cartLine => {
        const qtyOnHand = cartLine?.qtyOnHand ?? 0;
        // Condition to show stock as "DirectShip" when total qty<=0 and isDirectShipFromManufacturer is true
        const availabilityMessage = Number(qtyOnHand) > 0
            ? "In Stock"
            : cartLine?.properties?.isDirectShipFromManufacturer.toLowerCase() === "true"
                ? "DirectShip" : "Out Of Stock";
                const hazardousProduct = cartLine?.properties?.isHazardousGood.toLowerCase() === 'true' ? "Yes" : "";
                const eventData = {
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            price: cartLine?.pricing?.actualPrice,
            quantity: cartLine.qtyOrdered,
            category: cartLine?.properties?.cartlineCategoryName,
            list: cartLine?.properties?.productSourceActionField || "",
            listid: cartLine?.properties?.listIdField || "",
            position: cartLine?.properties?.productListPosition || "",
            stock: availabilityMessage,
            brand: cartLine?.properties?.cartLineBrandName,
            condition: cartLine?.properties?.cartLineCondition,
            vendorstockqty: cartLine?.qtyOnHand,
            Hazardous: hazardousProduct,
        };
        dataLayerProducts.push(eventData);
        });
    let latestCartCheckoutEvent = null;
    for (let i = dataLayer.length - 1; i >= 0; i--) {
        if (dataLayer[i]?.event) {
            latestCartCheckoutEvent = dataLayer[i].event;
            break;
        }
    }
    if (latestCartCheckoutEvent && latestCartCheckoutEvent.event === 'checkout' && latestCartCheckoutEvent?.ecommerce?.step === 1) {
        latestCartCheckoutEvent?.ecommerce?.detail?.products?.pop();
        latestCartCheckoutEvent?.ecommerce?.detail?.products.push({ dataLayerProducts });
    }
    else {
        const data = {
            'checkout': {
                'actionField': { 'step': 1, 'option': 'proceed to checkout', 'action': 'checkout' },
                'products': dataLayerProducts,
                'cartId': cart?.orderNumber
            }
        };
        const ecommerceObj = { 'event': 'checkout', ecommerce: data };
        dataLayer.push({ ecommerce: null });
        dataLayer.push(ecommerceObj);
    }
}
export function checkoutShippingTracking(cart: CartModel) {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }
    const dataLayerProducts: any[] = [];
    cart?.cartLines?.forEach(cartLine => {
        const isHazardous = cartLine?.properties?.isHazardousGood.toLowerCase() === 'true' ? 'Yes' : '';
        dataLayerProducts.push({
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            price: cartLine?.pricing?.actualPrice,
            quantity: cartLine.qtyOrdered,
            category: cartLine?.properties?.cartlineCategoryName,
            list: cartLine?.properties?.productSourceActionField || "",
            listid: cartLine?.properties?.listIdField || "",
            position: cartLine?.properties?.productListPosition || "",
            brand: cartLine?.properties?.cartLineBrandName,
            condition: cartLine?.properties?.cartLineCondition,
            Hazardous: isHazardous
        });
    });
    const data = {
        'checkout': {
            'actionField': { 'step': 2, 'option': 'address', 'action': 'checkout' },
            'products': dataLayerProducts,
            'cartId': cart?.orderNumber
        }
    };
    const ecommerceObj = { 'event': 'checkout', ecommerce: data };
    dataLayer.push({ ecommerce: null });
    dataLayer.push(ecommerceObj);
}
export function checkoutReviewAndPayTracking(cart?: Readonly<Cart>) {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }
    const dataLayerProducts: any[] = [];
    cart?.cartLines?.forEach(cartLine => {
        const isHazardous = cartLine?.properties?.isHazardousGood.toLowerCase() === 'true' ? 'Yes' : '';
        dataLayerProducts.push({
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            price: cartLine?.pricing?.actualPrice,
            quantity: cartLine.qtyOrdered,
            category: cartLine?.properties?.cartlineCategoryName,
            list: cartLine?.properties?.productSourceActionField || "",
            listid: cartLine?.properties?.listIdField || "",
            position: cartLine?.properties?.productListPosition || "",
            brand: cartLine?.properties?.cartLineBrandName,
            condition: cartLine?.properties?.cartLineCondition,
            Hazardous: isHazardous
        });
    });
    const data = {
        'checkout': {
            'actionField': { 'step': 3, 'option': 'review', 'action': 'checkout' },
            'products': dataLayerProducts,
            'cartId': cart?.orderNumber
        }
    };
    const ecommerceObj = { 'event': 'checkout', ecommerce: data };
    dataLayer.push({ ecommerce: null });
    dataLayer.push(ecommerceObj);
}
export function sharedCartTracking(state: ApplicationState, cart?: Readonly<Cart>) {
    const dataLayerProducts: any[] = [];
    cart?.cartLines?.forEach(cartLine => {
        let productCondition = "";
        if (cartLine.productId) {
            const productData = getProductState(state, cartLine.productId).value;
            productCondition = productData?.properties?.condition || cartLine?.properties?.cartLineCondition;
        }
        dataLayerProducts.push({
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            price: cartLine?.pricing?.actualPrice,
            quantity: cartLine.qtyOrdered,
            brand: cartLine?.properties?.cartLineBrandName,
            condition: productCondition,
            category: cartLine?.properties?.cartlineCategoryName,
            list: cartLine?.properties?.productSourceActionField || "",
            position: cartLine?.properties?.productListPosition || "",
        });
    });
    const dataLayer = (window as any).dataLayer || [];
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
        event: "shareCart",
        ecommerce: {
            currencyCode: state.context.session.currency?.currencyCode,
            add: {
                actionField:
                {
                    list: "Cart",
                    action: "click",
                },
                products: dataLayerProducts
            },
        },
    });
}
export function sendShareCartTracking(listid: string, state: ApplicationState, cart?: Readonly<Cart>) {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }
    const dataLayerProducts: any[] = [];
    cart?.cartLines?.forEach(cartLine => {
        dataLayerProducts.push({
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            price: cartLine?.pricing?.actualPrice,
            quantity: cartLine.qtyOrdered,
            brand: cartLine?.properties?.cartLineBrandName,
            category: cartLine?.properties?.cartlineCategoryName,
            list: cartLine?.properties?.productSourceActionField || "",
            position: cartLine?.properties?.productListPosition || "",
        });
    });
    const shareCartEvent = {
        event: "shareCart",
        ecommerce: {
            currencyCode: state.context.session.currency?.currencyCode,
            add: {
                actionField:
                {
                    id: listid,
                    list: "Shared Cart",
                    action: "send",
                },
                products: dataLayerProducts,
            },
        },
    };
    dataLayer.push({ ecommerce: null });
    dataLayer.push(shareCartEvent);
}
export function getVendorStockStatus(product: ProductModel | CartLineModel, productInfo: ProductInfo) {
    let vendorStockQty = 0;
    let vendorStockStatus = "";
    if (productInfo?.inventory?.inventoryWarehousesDtos) {
        const warehouseDto = productInfo?.inventory?.inventoryWarehousesDtos[0].warehouseDtos;
        if (warehouseDto) {
            for (const warehouseData of warehouseDto) {
                if (warehouseData.name === "Vendor Stock") {
                    // setvendorStockQty(warehouseData.qty);
                    vendorStockQty = warehouseData.qty;
                }
                if (warehouseData.qty > 0 && warehouseData.name === "Blind Ship") {
                    // setVendorStockStatus("blindship");
                    vendorStockStatus = "blindship";
                    break;
                }
                if (product.properties.isDirectShipFromManufacturer?.toLowerCase() === "false") {
                    if ((warehouseData.qty > 0 && warehouseData.name === "Vendor Stock") || (warehouseData.qty === 0 && warehouseData.name === "Blind Ship")) {
                        // setVendorStockStatus("crossdock");
                        vendorStockStatus = "crossdock";
                        break;
                    }
                }
            }
        }
    }
    if (product.properties.isDirectShipFromManufacturer?.toLowerCase() === "true" && vendorStockStatus === "") {
        // setVendorStockStatus("directship");
        vendorStockStatus = "directship";
    }
    return { vendorStockQty, vendorStockStatus };
}